import {
  AffiliationAdminRoleIdsEnum,
  AffiliationManagerRoleIdsEnum,
  AllUserRoleIdsEnum,
  InternalAdminRoleIdsEnum,
  InternalContentRoleIdsEnum,
  StudentRoleIdsEnum,
  TeacherRoleIdsEnum,
  TutorRoleIdsEnum,
  TutoringAdminRoleIdsEnum,
  TutoringStudentRoleIdsEnum,
  UserGroupRolesEnum,
} from "@/enums/userManagement";

// SP1 (BR-UI-1002 / AQ-7): AEM→AFFILIATION_MANAGER rename; +5 new roles slotted below.
// plan-13 REV-1: role IDs now live in 9 per-group enums — each member is qualified by its group.
// Derived from the merged AllUserRoleIdsEnum so it always reflects the new per-group role IDs.
export const AllUserRoleIds: ReadonlyArray<number> = Object.freeze(
  Object.values(AllUserRoleIdsEnum).filter((value): value is number => typeof value === "number"),
);

// The taxonomy behind AllUserRoleIdsEnum, spelled out: which role-ID enum belongs to which group
// role. Keyed by UserGroupRolesEnum so a tenth group role fails to compile until its roles are
// slotted in here. Values stay `object` because a numeric enum also carries reverse mappings —
// getIdNamePairsFromMatchingEnums() is what turns one into { Id, Name } pairs.
export const ROLE_IDS_ENUM_BY_GROUP_ROLE: Readonly<Record<UserGroupRolesEnum, object>> =
  Object.freeze({
    [UserGroupRolesEnum.STUDENT_ROLES]: StudentRoleIdsEnum,
    [UserGroupRolesEnum.AFFILIATION_ADMIN_ROLES]: AffiliationAdminRoleIdsEnum,
    [UserGroupRolesEnum.TEACHER_ROLES]: TeacherRoleIdsEnum,
    [UserGroupRolesEnum.INTERNAL_ADMIN_ROLES]: InternalAdminRoleIdsEnum,
    [UserGroupRolesEnum.AFFILIATION_MANAGER_ROLES]: AffiliationManagerRoleIdsEnum,
    [UserGroupRolesEnum.INTERNAL_CONTENT_ROLES]: InternalContentRoleIdsEnum,
    [UserGroupRolesEnum.TUTORING_ADMIN_ROLES]: TutoringAdminRoleIdsEnum,
    [UserGroupRolesEnum.TUTOR_ROLES]: TutorRoleIdsEnum,
    [UserGroupRolesEnum.TUTORING_STUDENT_ROLES]: TutoringStudentRoleIdsEnum,
  });

// The Tabler glyph (no "ti-" prefix) each group role is drawn with, so any grouped role list labels
// its groups the same way without every call site inventing its own icons.
export const GROUP_ROLE_ICON_NAMES: Readonly<Record<UserGroupRolesEnum, string>> = Object.freeze({
  [UserGroupRolesEnum.STUDENT_ROLES]: "school",
  [UserGroupRolesEnum.AFFILIATION_ADMIN_ROLES]: "building",
  [UserGroupRolesEnum.TEACHER_ROLES]: "chalkboard",
  [UserGroupRolesEnum.INTERNAL_ADMIN_ROLES]: "shield-lock",
  [UserGroupRolesEnum.AFFILIATION_MANAGER_ROLES]: "briefcase",
  [UserGroupRolesEnum.INTERNAL_CONTENT_ROLES]: "book",
  [UserGroupRolesEnum.TUTORING_ADMIN_ROLES]: "clipboard-list",
  [UserGroupRolesEnum.TUTOR_ROLES]: "user-star",
  [UserGroupRolesEnum.TUTORING_STUDENT_ROLES]: "user-heart",
});

// The student roles that get the COMPLETE student feature set. BASIC_STUDENT (18) is deliberately
// absent: its navbar config exposes only Self Practice, the two student reports, the dashboard and
// the profile, so this array is what gates every student route it must NOT reach (Assignments,
// Institute Tests, Self Tests, Learn, Live Classes, Past Year Papers, Interactive Videos…).
export const FullAccessStudentRoleIds: ReadonlyArray<number> = Object.freeze([
  StudentRoleIdsEnum.B2C_STUDENT,
  StudentRoleIdsEnum.DEMO_STUDENT,
  StudentRoleIdsEnum.OLYMPIAD_STUDENT,
  StudentRoleIdsEnum.STEMLEARN_STUDENT,
  StudentRoleIdsEnum.STUDENT,
]);

// Basic Student (18) — the cut-down student role. Dedicated array per the SCHOOL_PARTNER_RM_IDS
// pattern: concat it onto the specific routes its navbar config lists rather than widening
// FullAccessStudentRoleIds, which would silently hand it the whole student app.
export const BASIC_STUDENT_ROLE_IDS: ReadonlyArray<number> = Object.freeze([
  StudentRoleIdsEnum.BASIC_STUDENT,
]);

// The canonical "this user is a student" set — mirrors the backend's ALL_STUDENT_ROLE_IDS. Drives
// IsStudent (enrolled-course bootstrap, MustChangePassword exemption, offline-download chrome), the
// Manage Users students tab and the role-based loading copy. Route gating uses the two arrays above.
export const AllStudentRoleIds: ReadonlyArray<number> = Object.freeze(
  FullAccessStudentRoleIds.concat(BASIC_STUDENT_ROLE_IDS),
);

export const TutoringStudentRoleIds: ReadonlyArray<number> = Object.freeze([
  TutoringStudentRoleIdsEnum.TUTORING_STUDENT,
]);

// Teaching roles that also carry tutoring access — this array gates the tutoring tutor routes, so
// BASIC_TEACHER (32) stays out of it. Use AllTeachingRoleIds for "is this user a teacher?" checks.
export const AllTeacherRoleIds: ReadonlyArray<number> = Object.freeze([
  InternalContentRoleIdsEnum.FACULTY,
  TeacherRoleIdsEnum.SCHOOL_TEACHER,
  TutorRoleIdsEnum.TUTOR,
]);

// Basic Teacher (32) — the cut-down teacher role. Its navbar config lists only the teacher
// dashboard and the two batch reports; concat this onto those routes rather than widening
// AllTeacherRoleIds / AllAffiliationRoleIds, which gate the rest of the affiliation app.
export const BASIC_TEACHER_ROLE_IDS: ReadonlyArray<number> = Object.freeze([
  TeacherRoleIdsEnum.BASIC_TEACHER,
]);

// Every role that teaches, Basic Teacher included. The canonical "is this user a teacher?" set —
// kept separate from AllTeacherRoleIds because that one doubles as a tutoring route gate.
export const AllTeachingRoleIds: ReadonlyArray<number> = Object.freeze(
  AllTeacherRoleIds.concat(BASIC_TEACHER_ROLE_IDS),
);

export const AllAdminRoleIds: ReadonlyArray<number> = Object.freeze([
  InternalAdminRoleIdsEnum.ADMIN,
]);
export const AemRoleIds: ReadonlyArray<number> = Object.freeze([
  AffiliationManagerRoleIdsEnum.AFFILIATION_MANAGER,
  InternalAdminRoleIdsEnum.ADMIN,
]);

// REQ-024 (affiliation-programs-manager-1a): the AEM Programs route admits Principal
// READ-ONLY. Dedicated array per the usermgmt-8-4/OQ-37 pattern — never widen the shared
// AemRoleIds (it gates other AEM routes); mutation gating in the view stays on AemRoleIds.
export const AemProgramsRoleIds: ReadonlyArray<number> = Object.freeze([
  AffiliationManagerRoleIdsEnum.AFFILIATION_MANAGER,
  InternalAdminRoleIdsEnum.ADMIN,
  AffiliationAdminRoleIdsEnum.PRINCIPAL,
]);

// HotFix school-partner-rm-review-access: School Partner RM (52) may VIEW the six school review
// report routes. Dedicated array per the usermgmt-8-4/OQ-37 pattern — do NOT widen the shared
// AemRoleIds / AllManagerRoleIds / AllAffiliationRoleIds arrays (they gate other features).
export const SCHOOL_PARTNER_RM_IDS: ReadonlyArray<number> = Object.freeze([
  AffiliationManagerRoleIdsEnum.SCHOOL_PARTNER_RM,
]);

// The school-leadership roles an AEM (51) owns end to end — Principal (23) / KDM (24) / CSM (26).
// AEMs onboard and run these users for their schools, so Manage Users lets them filter, add and
// edit them on top of the shared AddEditUserRoleIds set. Dedicated array per the SCHOOL_PARTNER_RM
// pattern: do NOT widen AddEditUserRoleIds itself, which every other non-admin role reads.
export const AEM_MANAGEABLE_ROLE_IDS: ReadonlyArray<number> = Object.freeze([
  AffiliationAdminRoleIdsEnum.PRINCIPAL,
  AffiliationAdminRoleIdsEnum.KDM,
  AffiliationAdminRoleIdsEnum.CSM,
]);

export const AllAemRoleIds: ReadonlyArray<number> = Object.freeze([
  AffiliationManagerRoleIdsEnum.AFFILIATION_MANAGER,
  InternalAdminRoleIdsEnum.ADMIN,
  AffiliationManagerRoleIdsEnum.SCHOOL_PARTNER_RM,
]);

export const AffiliationHeadRoleIds: ReadonlyArray<number> = Object.freeze([
  AffiliationAdminRoleIdsEnum.AFFILIATION_HEAD,
  AffiliationAdminRoleIdsEnum.KDM,
  AffiliationAdminRoleIdsEnum.PRINCIPAL,
  AffiliationAdminRoleIdsEnum.SPOC,
  AffiliationAdminRoleIdsEnum.CSM,
]);

// AEM staff (Manager 51 / Admin 41 / RM 52) + the school-side users who view their own school's
// pages: the full affiliation-head group (Head 27 / KDM 24 / Principal 23 / SPOC 25 / CSM 26) +
// School Teacher (31). Gates the school-scoped AEM routes (tickets / lifecycle / onboarding) and
// ModalTicketDetail's role-gated school link (BR-UI-016). aem-0-1 / BR C-1 / CR-2.
export const AEM_SCHOOL_SCOPED_ROLES: ReadonlyArray<number> = Object.freeze(
  AllAemRoleIds.concat(AffiliationHeadRoleIds, [TeacherRoleIdsEnum.SCHOOL_TEACHER]),
);

// Who may send a finished test's report to students by SMS / email: the affiliation-admin
// group (21–27) + School Teacher (31). The SpeEdLabs-internal ADMIN (41) is deliberately
// absent — the backend 403s it, so the button is hidden rather than shown disabled. The
// school itself is resolved from the token; there is no affiliationId anywhere in the flow.
export const SEND_ACTIVITY_REPORT_ROLE_IDS: ReadonlyArray<number> = Object.freeze([
  AffiliationAdminRoleIdsEnum.CENTER_MANAGER,
  AffiliationAdminRoleIdsEnum.B2C_INDIVIDUAL_TEACHER,
  AffiliationAdminRoleIdsEnum.PRINCIPAL,
  AffiliationAdminRoleIdsEnum.KDM,
  AffiliationAdminRoleIdsEnum.SPOC,
  AffiliationAdminRoleIdsEnum.CSM,
  AffiliationAdminRoleIdsEnum.AFFILIATION_HEAD,
  TeacherRoleIdsEnum.SCHOOL_TEACHER,
]);

export const AllAffiliationRoleIds: ReadonlyArray<number> = Object.freeze([
  AffiliationAdminRoleIdsEnum.AFFILIATION_HEAD,
  AffiliationAdminRoleIdsEnum.KDM,
  AffiliationAdminRoleIdsEnum.PRINCIPAL,
  AffiliationAdminRoleIdsEnum.SPOC,
  AffiliationAdminRoleIdsEnum.CSM,
  InternalContentRoleIdsEnum.FACULTY,
  TeacherRoleIdsEnum.SCHOOL_TEACHER,
  TutorRoleIdsEnum.TUTOR,
]);

// usermgmt-8-4 (OQ-37): dedicated array for the member-list route — affiliation admins (21–27)
// + internal admin portal (41). Intentionally narrower than AllManagerRoleIds to avoid over-grant.
export const ManageAffiliationUsersRoleIds: ReadonlyArray<number> = Object.freeze([
  AffiliationAdminRoleIdsEnum.CENTER_MANAGER,
  AffiliationAdminRoleIdsEnum.B2C_INDIVIDUAL_TEACHER,
  AffiliationAdminRoleIdsEnum.PRINCIPAL,
  AffiliationAdminRoleIdsEnum.KDM,
  AffiliationAdminRoleIdsEnum.SPOC,
  AffiliationAdminRoleIdsEnum.CSM,
  AffiliationAdminRoleIdsEnum.AFFILIATION_HEAD,
]);

// NOTE (OQ-37): the member-list route widening to the new affiliation-admin roles
// (PRINCIPAL/KDM/SPOC/CSM) is handled at the route level in usermgmt-8-4, NOT by mutating this
// shared array (which gates other features too — avoid an over-grant). Rename-only here.
export const AllManagerRoleIds: ReadonlyArray<number> = Object.freeze([
  AffiliationManagerRoleIdsEnum.AFFILIATION_MANAGER,
  InternalAdminRoleIdsEnum.ADMIN,
  AffiliationAdminRoleIdsEnum.AFFILIATION_HEAD,
  AffiliationAdminRoleIdsEnum.CENTER_MANAGER,
  TutoringAdminRoleIdsEnum.TUTORING_ADMIN,
]);

export const AllTutoringAdminRoleIds: ReadonlyArray<number> = Object.freeze([
  InternalAdminRoleIdsEnum.ADMIN,
  TutoringAdminRoleIdsEnum.TUTORING_ADMIN,
]);

export const AllTutoringOpsRoleIds: ReadonlyArray<number> = Object.freeze([
  TutoringAdminRoleIdsEnum.TUTORING_ADMIN,
  TutoringAdminRoleIdsEnum.TUTORING_OPS,
  TutoringAdminRoleIdsEnum.TUTORING_SALES,
]);

export const AllContentRoleIds: ReadonlyArray<number> = Object.freeze([
  InternalAdminRoleIdsEnum.ADMIN,
  InternalContentRoleIdsEnum.CONTENT_MANAGER,
  InternalContentRoleIdsEnum.FACULTY,
]);

// SpeEdLabs-internal roles — mirrors the backend's ALL_INTERNAL_ROLE_IDS. These users hold NO
// AffiliationUser row (the 0041 cutover deletes them), so get-affiliation-users can never return
// them however the role filter is set. Drives the Manage Users "SpeEdLabs Team" tab, and is what
// keeps these roles OUT of the affiliation-scoped Other Roles picker where they silently
// returned nothing.
export const InternalUserRoleIds: ReadonlyArray<number> = Object.freeze([
  InternalAdminRoleIdsEnum.ADMIN,
  AffiliationManagerRoleIdsEnum.AFFILIATION_MANAGER,
  AffiliationManagerRoleIdsEnum.SCHOOL_PARTNER_RM,
  InternalContentRoleIdsEnum.CONTENT_MANAGER,
  InternalContentRoleIdsEnum.FACULTY,
  TutoringAdminRoleIdsEnum.TUTORING_ADMIN,
  TutoringAdminRoleIdsEnum.TUTORING_OPS,
  TutoringAdminRoleIdsEnum.TUTORING_SALES,
]);

export const ContentApproverRoleIds: ReadonlyArray<number> = Object.freeze([
  InternalAdminRoleIdsEnum.ADMIN,
  InternalContentRoleIdsEnum.FACULTY,
]);

// QR code library bulk fan-out writes mapping rows against QR codes already printed in students'
// books, so it is withheld from Faculty in the UI. This is a UI-ONLY guard — the backend's
// POST /qr-code-library/qr-codes/bulk-map still accepts role 62, so it is not a security boundary.
export const QR_BULK_FAN_OUT_ROLE_IDS: ReadonlyArray<number> = Object.freeze([
  InternalAdminRoleIdsEnum.ADMIN,
  InternalContentRoleIdsEnum.CONTENT_MANAGER,
]);

export const AddEditUserRoleIds: ReadonlyArray<number> = Object.freeze([
  StudentRoleIdsEnum.STUDENT,
  StudentRoleIdsEnum.DEMO_STUDENT,
  TeacherRoleIdsEnum.SCHOOL_TEACHER,
  AffiliationAdminRoleIdsEnum.AFFILIATION_HEAD,
]);

export const AllOmrReviewRoleIds: ReadonlyArray<number> = Object.freeze([
  InternalAdminRoleIdsEnum.ADMIN,
  AffiliationManagerRoleIdsEnum.AFFILIATION_MANAGER,
  InternalContentRoleIdsEnum.CONTENT_MANAGER,
  InternalContentRoleIdsEnum.FACULTY,
]);

// SP1 (BR-UI-1003): SPOC/KDM/PRINCIPAL are now top-level roles — re-pointed off the retired
// AffiliationUserRoleIdEnum (CSM intentionally excluded, preserving the prior "external" membership).
export const ExternalAffiliationUserRoleIds: ReadonlyArray<number> = Object.freeze([
  AffiliationAdminRoleIdsEnum.SPOC,
  AffiliationAdminRoleIdsEnum.KDM,
  AffiliationAdminRoleIdsEnum.PRINCIPAL,
]);
